/**
 * One editable env row in the onboarding wizard (#34): a key from the probed
 * `.env.example`, the (editable) value, and whether it is treated as a secret.
 * `comment` is the parsed hint from the `.env.example` (#4); `include` lets the
 * operator deselect a parameter so it is not sent at all — e.g. for values the
 * app generates itself (#139).
 */
export interface EnvDraft {
  key: string;
  value: string;
  isSecret: boolean;
  comment: string | null;
  include: boolean;
}

const ENV_KEY_RE = /^[A-Z][A-Z0-9_]*$/;

/**
 * Map the wizard's editable env rows to the onboarding payload's plain `env`.
 * Secret-flagged rows are skipped — they go into `env_secrets` via
 * {@link secretsFromDraft}. Non-secret keys with a non-empty value become plain
 * `env`; keys that don't match the runtime env-key shape are dropped.
 */
export function envFromDraft(draft: EnvDraft[]): { env: Record<string, string> } {
  const env: Record<string, string> = {};
  for (const d of draft) {
    if (!d.include) continue;
    if (!ENV_KEY_RE.test(d.key)) continue;
    if (d.isSecret) continue;
    if (d.value !== "") env[d.key] = d.value;
  }
  return { env };
}

/**
 * Collects non-empty secret rows into the `env_secrets` map of the onboarding
 * payload (#100). Values are stored by the Hub sealed with AES-256-GCM
 * (ADR-019); empty values and keys that do not match the runtime env-key shape
 * are dropped.
 */
export function secretsFromDraft(draft: EnvDraft[]): { env_secrets: Record<string, string> } {
  const env_secrets: Record<string, string> = {};
  for (const d of draft) {
    if (!d.include) continue;
    if (!ENV_KEY_RE.test(d.key)) continue;
    if (!d.isSecret) continue;
    if (d.value !== "") env_secrets[d.key] = d.value;
  }
  return { env_secrets };
}

/**
 * One editable volume row in the onboarding wizard (#7). The full volume name is
 * built as `appdata-<slug>-<suffix>` only at submit time, so editing the slug in
 * the review step never leaves a stale, prefix-mismatched name behind.
 */
export interface VolumeDraft {
  suffix: string;
  container_path: string;
  include: boolean;
}

const VOLUME_SUFFIX_RE = /[^a-z0-9-]+/g;

/**
 * Derive a volume-name suffix from a declared container path: the last path
 * segment, lowercased and reduced to `[a-z0-9-]`. Falls back to `data`.
 */
export function suffixFromPath(path: string): string {
  const seg = path.split("/").filter(Boolean).pop() ?? "";
  const s = seg
    .toLowerCase()
    .replace(VOLUME_SUFFIX_RE, "-")
    .replace(/^-+|-+$/g, "");
  return s || "data";
}

/**
 * Map the wizard's volume rows to the onboarding payload's `runtime.volumes`.
 * Builds `appdata-<slug>-<suffix>` names, drops deselected/empty rows, and
 * deduplicates by built name. Always read-write (`read_only: false`).
 */
export function volumesFromDrafts(
  drafts: VolumeDraft[],
  slug: string,
): { volumes: Array<{ name: string; container_path: string; read_only: boolean }> } {
  const seen = new Set<string>();
  const volumes: Array<{ name: string; container_path: string; read_only: boolean }> = [];
  for (const d of drafts) {
    if (!d.include) continue;
    const suffix = d.suffix.trim();
    const container_path = d.container_path.trim();
    if (!suffix || !container_path) continue;
    const name = `appdata-${slug}-${suffix}`;
    if (seen.has(name)) continue;
    seen.add(name);
    volumes.push({ name, container_path, read_only: false });
  }
  return { volumes };
}
